import { useState } from 'react';
import type { Exercise } from '@jackedlogs/shared';

import type { DraftEntry, LoggerDispatch } from './state/loggerDraft';
import {
  seedKgFromSets,
  formatPlateWeight,
  resolveApplyTargetSetIdx,
} from './plateCalcMath';

// Owns the plate-calculator modal: state + the seed/apply orchestration.
// Extracted verbatim out of LoggerScreen (Track R); the seed/format/
// target-resolution rules live in plateCalcMath (pure, tested). The
// screen wires openForSet into the set rows and state/apply/close into
// PlateCalcModal.
type PlateCalcState = {
  visible: boolean;
  targetKg: number;
  exercise: Exercise | null;
  entryIdx: number | null;
  /** Set the modal was opened from (1.66.5: inbox 8c6a9371). Apply
   *  writes back to this set rather than guessing the "next pending"
   *  one, so the calc round-trips through the row the user was
   *  actually editing. Null on legacy entry-level opens. */
  setIdx: number | null;
  /** Bodyweight mode 2026-05-23 P.M. — drives the modal's sign toggle
   *  and the apply branch that writes the EXTRA value + extraNegative to
   *  the target set (vs the weighted branch that writes only weight). */
  isBodyweight: boolean;
  /** Pre-seeded sign so reopening the modal on an already-loaded assist
   *  set restores the Assist/Weighted choice. */
  initialNegative: boolean;
};

const INITIAL: PlateCalcState = {
  visible: false,
  targetKg: 0,
  exercise: null,
  entryIdx: null,
  setIdx: null,
  isBodyweight: false,
  initialNegative: false,
};

export function usePlateCalc({
  entries,
  catalog,
  dispatch,
}: {
  entries: DraftEntry[];
  catalog: Exercise[] | null;
  dispatch: LoggerDispatch;
}) {
  const [state, setState] = useState<PlateCalcState>(INITIAL);

  /** Seed the calculator from the set the user tapped Plates on and open
   *  the modal. Inbox 8c6a9371 (1.66.5): seed from the editing set, not
   *  the last non-zero set, so the calc reflects what the lifter sees.
   *  Apply then writes back to that same set. */
  const openForSet = (entryIdx: number, setIdx: number) => {
    const entry = entries[entryIdx];
    // Bodyweight gate removed 2026-05-23 P.M. — PlateCalc now handles
    // bodyweight + sign so the modal must open for every entry kind.
    if (!entry) return;
    const seedKg = seedKgFromSets(entry.sets, setIdx);
    const exercise = catalog?.find((ex) => ex.slug === entry.exerciseId) ?? null;
    const seedSet = entry.sets[setIdx] ?? entry.sets[entry.sets.length - 1];
    setState({
      visible: true,
      targetKg: seedKg,
      exercise,
      entryIdx,
      setIdx,
      isBodyweight: entry.isBodyweight,
      initialNegative: seedSet?.extraNegative ?? false,
    });
  };

  const close = () => setState((s) => ({ ...s, visible: false }));

  /** Plate-calc → set weight (inbox a0c75e23 + 8c6a9371). Writes the
   *  chosen total kg back to the set the modal was opened from. Falls
   *  back to the next-pending set when the modal carries no setIdx
   *  (legacy entry-level open path). Always closes the modal. */
  // Signed kg in: weighted-mode emits positive, bodyweight-mode emits
  // signed (positive = weighted-pull-up extra, negative = band assist).
  const apply = (signedKg: number) => {
    setState((s) => ({ ...s, visible: false }));
    if (!Number.isFinite(signedKg) || signedKg === 0) return;
    const entryIdx = state.entryIdx;
    if (entryIdx === null) return;
    const entry = entries[entryIdx];
    if (!entry) return;
    const setIdx = resolveApplyTargetSetIdx(entry.sets, state.setIdx);
    const targetSet = entry.sets[setIdx];
    if (!targetSet) return;
    const formatted = formatPlateWeight(Math.abs(signedKg), targetSet.unit);
    if (entry.isBodyweight) {
      // Bodyweight branch — write the EXTRA magnitude into weight (per
      // the longstanding "EXTRA doubles as weight" convention) AND
      // propagate the sign via the toggle_sign reducer when needed.
      dispatch({
        type: 'update_set',
        entryIdx,
        setIdx,
        patch: { weight: formatted },
      });
      const negativeNow = signedKg < 0;
      if (negativeNow !== targetSet.extraNegative) {
        dispatch({ type: 'toggle_sign', entryIdx, setIdx });
      }
      return;
    }
    // Weighted branch — sign is ignored; modal won't emit negative here.
    dispatch({
      type: 'update_set',
      entryIdx,
      setIdx,
      patch: { weight: formatted },
    });
  };

  return { state, openForSet, apply, close };
}
